/**
 * Versão nova do ECO no ar: quando a página aberta pode trocar sozinha.
 *
 * Cada deploy publica `/version.txt` com o commit que está sendo servido, e o
 * build carimba o mesmo commit no bundle (VITE_APP_VERSION, ver Dockerfile).
 * Quando os dois divergem, a aba está rodando código velho. Até 01/10/2026 só
 * saía disso quem apertava Ctrl+R ou fechava o app desktop — e o desktop, que
 * fica aberto o dia inteiro no chat, era justamente quem nunca atualizava.
 *
 * A regra é trocar sem ninguém perceber e sem perder o que foi digitado:
 *
 *  - ao mudar de tela: sair da página já descarta o estado dela, então
 *    carregar a tela nova pelo servidor não custa nada a ninguém;
 *  - com a janela escondida (minimizada, atrás de outra, na bandeja) há alguns
 *    minutos — o atraso é para não pegar um alt-tab rápido nem um envio de
 *    arquivo em andamento;
 *  - sem uso há bastante tempo, com a janela à vista (o monitor do lado, a TV).
 *
 * Os dois últimos só valem se não houver campo preenchido na tela: formulário
 * pela metade espera a troca de tela ou o botão do aviso.
 */

/** Commit que o build carimbou neste bundle. Vazio em desenvolvimento. */
export const VERSAO_DO_BUNDLE: string = import.meta.env.VITE_APP_VERSION ?? '';

/** Escondida há este tempo, a janela pode recarregar. */
export const OCULTO_MS = 2 * 60_000;
/** Sem mouse nem teclado há este tempo, a janela pode recarregar. */
export const OCIOSO_MS = 15 * 60_000;

/**
 * Só commit de verdade conta. Em desenvolvimento o `/version.txt` não existe e
 * o Vite responde com o index.html; no build sem SHA o Dockerfile grava
 * "desconhecido". Nos dois casos não há o que comparar.
 */
export function ehVersaoValida(v: string | null | undefined): v is string {
  return !!v && /^[0-9a-f]{7,40}$/i.test(v.trim());
}

export function versaoNova(doBundle: string, servida: string | null | undefined): boolean {
  if (!ehVersaoValida(doBundle) || !ehVersaoValida(servida)) return false;
  return doBundle.trim().toLowerCase() !== servida.trim().toLowerCase();
}

export type MomentoDaJanela = {
  agora: number;
  /** Desde quando a janela está escondida; null se está à vista. */
  ocultaDesde: number | null;
  ultimaAtividade: number;
  temCampoPreenchido: boolean;
};

/** Sem troca de tela: pode recarregar agora? */
export function podeRecarregarSozinha(m: MomentoDaJanela): boolean {
  if (m.temCampoPreenchido) return false;
  if (m.ocultaDesde !== null && m.agora - m.ocultaDesde >= OCULTO_MS) return true;
  return m.agora - m.ultimaAtividade >= OCIOSO_MS;
}

/**
 * Campo que, recarregando, perderia o que foi digitado. Recebe os elementos em
 * que houve digitação; os que saíram da tela (diálogo fechado depois de
 * salvar) não contam. Quem guarda o próprio rascunho marca o campo, ou um
 * ancestral, com `data-sobrevive-recarga` e não entra na conta.
 */
export function temCampoPreenchido(editados: Iterable<Element>): boolean {
  for (const el of editados) {
    if (!el.isConnected || el.closest('[data-sobrevive-recarga]')) continue;
    if (el instanceof HTMLInputElement) {
      if (el.type === 'checkbox' || el.type === 'radio') return true;
      if (el.value.trim() !== '') return true;
    } else if (el instanceof HTMLTextAreaElement) {
      if (el.value.trim() !== '') return true;
    } else if (el instanceof HTMLSelectElement) {
      return true;
    } else if ((el as HTMLElement).isContentEditable) {
      if ((el.textContent ?? '').trim() !== '') return true;
    }
  }
  return false;
}
